import React, { useEffect, useState } from "react";
import { Button, Form, Row, Col, Card, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import apiCall from "./../../API/API";
import ConfirmModal from "../../Component/ConfirmModel"; // Ensure this exists

const FakeEntryGenerator = () => {
  const token = localStorage.getItem("token");

  const [shuffleUserLimit, setShuffleUserLimit] = useState(0);
  const [userLimit, setUserLimit] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [minOrderAmount, setMinOrderAmount] = useState(0);
  const [maxOrderAmount, setMaxOrderAmount] = useState(0);
  const [minWithdrawAmount, setMinWithdrawAmount] = useState(0);
  const [maxWithdrawAmount, setMaxWithdrawAmount] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [initialSettings, setInitialSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const config = {
          headers: {
            Authorization: token,
          },
        };
        const res = await apiCall("get", "/get-advance-info", {}, config);
        const data = res.data.data.fake_entries;

        const values = {
          shuffleUserLimit: data.shuffleUserLimit,
          userLimit: data.userLimit,
          totalEntries: data.totalEntries,
          minOrderAmount: data.minOrderAmount,
          maxOrderAmount: data.maxOrderAmount,
          minWithdrawAmount: data.minWithdrawAmount,
          maxWithdrawAmount: data.maxWithdrawAmount,
          enabled: data.enabled,
        };

        setShuffleUserLimit(values.shuffleUserLimit);
        setUserLimit(values.userLimit);
        setTotalEntries(values.totalEntries);
        setMinOrderAmount(values.minOrderAmount);
        setMaxOrderAmount(values.maxOrderAmount);
        setMinWithdrawAmount(values.minWithdrawAmount);
        setMaxWithdrawAmount(values.maxWithdrawAmount);
        setEnabled(values.enabled);
        setInitialSettings(values);
      } catch (err) {
        toast.error("Failed to load current settings.");
      }
    };

    fetchSettings();
  }, [token]);

  // 🔁 Track changes to form fields
  useEffect(() => {
    if (!initialSettings) return;

    const hasChanged =
      shuffleUserLimit !== initialSettings.shuffleUserLimit ||
      userLimit !== initialSettings.userLimit ||
      totalEntries !== initialSettings.totalEntries ||
      minOrderAmount !== initialSettings.minOrderAmount ||
      maxOrderAmount !== initialSettings.maxOrderAmount ||
      minWithdrawAmount !== initialSettings.minWithdrawAmount ||
      maxWithdrawAmount !== initialSettings.maxWithdrawAmount ||
      enabled !== initialSettings.enabled;

    setSettingsChanged(hasChanged);
  }, [
    shuffleUserLimit,
    userLimit,
    totalEntries,
    minOrderAmount,
    maxOrderAmount,
    minWithdrawAmount,
    maxWithdrawAmount,
    enabled,
    initialSettings,
  ]);

  const handleUpdateSettings = async () => {
    if (minOrderAmount > maxOrderAmount || minWithdrawAmount > maxWithdrawAmount) {
      toast.error("Minimum values cannot be greater than maximum values.");
      return;
    }

    try {
      setLoadingUpdate(true);
      const config = {
        headers: {
          Authorization: token,
        },
      };

      const payload = {
        shuffleUserLimit,
        userLimit,
        totalEntries,
        minOrderAmount,
        maxOrderAmount,
        minWithdrawAmount,
        maxWithdrawAmount,
        enabled,
      };

      const response = await apiCall("post", "/update-auto-entries", payload, config);
      toast.success(response.data.message || "Settings updated successfully.");
      setInitialSettings(payload);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update settings.");
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleGenerateEntries = async () => {
    try {
      setLoadingGenerate(true);
      const config = {
        headers: {
          Authorization: token,
        },
      };

      setShowModal(false);
      const response = await apiCall("get", "/generate-auto-entries", {}, config);
      toast.success(response?.message || "Fake entries generated successfully.");
    } catch (error) {
      toast.error(error?.response?.message || "Failed to generate fake entries.");
    } finally {
      setLoadingGenerate(false);
    }
  };

  return (
    <Card className="p-4 shadow-sm">
      <h3 className="text-center mb-4">Fake Entries Settings</h3>

      {/* 🔔 Warning alert */}
      {settingsChanged && (
        <Alert variant="warning">
          ⚠️ You have modified values. Please click <strong>Update Settings</strong> before generating fake entries.
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Shuffle User Limit</Form.Label>
            <Form.Control
              type="number"
              value={shuffleUserLimit}
              onChange={(e) => setShuffleUserLimit(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>User Entry Limit</Form.Label>
            <Form.Control
              type="number"
              value={userLimit}
              onChange={(e) => setUserLimit(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Total Entries (Each for Order & Withdrawal)</Form.Label>
            <Form.Control
              type="number"
              value={totalEntries}
              onChange={(e) => setTotalEntries(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Min Order Amount</Form.Label>
            <Form.Control
              type="number"
              value={minOrderAmount}
              onChange={(e) => setMinOrderAmount(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Max Order Amount</Form.Label>
            <Form.Control
              type="number"
              value={maxOrderAmount}
              onChange={(e) => setMaxOrderAmount(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Min Withdraw Amount</Form.Label>
            <Form.Control
              type="number"
              value={minWithdrawAmount}
              onChange={(e) => setMinWithdrawAmount(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Max Withdraw Amount</Form.Label>
            <Form.Control
              type="number"
              value={maxWithdrawAmount}
              onChange={(e) => setMaxWithdrawAmount(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-4">
        <Form.Check
          type="switch"
          label="Show on User Dashboard"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
      </Form.Group>

      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2">
        {/* {settingsChanged && ( */}
          <>
            <Button
              variant="secondary"
              disabled={!settingsChanged || loadingGenerate}
              onClick={() => {
                if (initialSettings) {
                  setShuffleUserLimit(initialSettings.shuffleUserLimit);
                  setUserLimit(initialSettings.userLimit);
                  setTotalEntries(initialSettings.totalEntries);
                  setMinOrderAmount(initialSettings.minOrderAmount);
                  setMaxOrderAmount(initialSettings.maxOrderAmount);
                  setMinWithdrawAmount(initialSettings.minWithdrawAmount);
                  setMaxWithdrawAmount(initialSettings.maxWithdrawAmount);
                  setEnabled(initialSettings.enabled);
                  toast.info("Changes reverted.");
                }
              }}
            >
              Cancel Changes
            </Button>
          </>
        {/* )} */}

        <Button
          variant="primary"
          onClick={handleUpdateSettings}
          disabled={loadingUpdate || !settingsChanged}
        >
          {loadingUpdate ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Updating...
            </>
          ) : (
            "Update Settings"
          )}
        </Button>
      </div>

        <Button
          variant="danger"
          onClick={() => setShowModal(true)}
          disabled={settingsChanged || loadingGenerate}
          title={settingsChanged ? "Please update settings first" : ""}
        >
          {loadingGenerate ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Generating...
            </>
          ) : (
            "Generate Entries Now"
          )}
        </Button>
      </div>


      {/* Confirm Modal */}
      <ConfirmModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleConfirm={handleGenerateEntries}
        title="Generate Fake Entries"
        body="Are you sure you want to generate fake orders and withdrawals?"
      />
    </Card>
  );
};

export default FakeEntryGenerator;
